import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationApi } from '../api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({ unreadCount: 0, refresh: () => {}, adjustCount: () => {} });

export const NotificationProvider = ({ children }) => {
  const { user, hasModuleAccess } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  // Notifications are entirely a MaterialWatch feature (the bell only even
  // renders in AppLayout's TopBar, never in Portal-only pages) — a user with
  // no MaterialWatch access has nothing to fetch, and polling anyway just
  // hits MaterialWatch's backend with a request it would 403/404 on forever
  // for as long as they're logged in. Gating here, not just at the bell,
  // stops the wasted polling even while sitting on a Portal-only page.
  const canSeeNotifications = hasModuleAccess('materialwatch');

  const refresh = useCallback(async () => {
    if (!user || !canSeeNotifications) return;
    try {
      const res = await notificationApi.unreadCount();
      setUnreadCount(res.data.data.count);
    } catch { /* silent */ }
  }, [user, canSeeNotifications]);

  // Adjust the badge count locally without an extra API call.
  // Use a negative delta to decrement (e.g. -1 when marking one read).
  const adjustCount = useCallback((delta) => {
    setUnreadCount((prev) => Math.max(0, prev + delta));
  }, []);

  useEffect(() => {
    if (!user || !canSeeNotifications) return;
    refresh();
    const interval = setInterval(refresh, 8000);
    return () => clearInterval(interval);
  }, [user, canSeeNotifications, refresh]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refresh, adjustCount }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
